import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import type { CategoryFormState } from '@openx/types/targeting/category.ts';
import { Intersect } from '@openx/types/targeting/targetingValuesTypes.ts';
import { noop } from '@openx/utils/noop.ts';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../utils/context/TargetingContext.tsx';
import { FiltersTitle } from '../shared/FiltersTitle/FiltersTitle.tsx';
import {
  TargetingDropdownSection,
  type TargetingDropdownSectionProps,
} from '../shared/ReadOnlyTargetingDropdown/TargetingDropdownSection.tsx';
import { SelectedItems } from '../shared/SelectedItems/SelectedItems.tsx';
import type { TargetingItemsProps } from '../types.ts';

export const CategoryFormPreviewItems = ({
  targetingParams,
  readonly = true,
  onOpenDrawerClick,
  onRemove,
  optionsCount,
  disabledReasonMessage,
}: TargetingItemsProps<CategoryFormState> & TargetingDropdownSectionProps) => {
  const { t } = useTranslation();
  const { useCategoriesFetch } = useTargetingContext();

  const { categories, refetchCategories, isCategoriesLoading } = useCategoriesFetch();

  const { op, val } = targetingParams;

  const typeLabel = op === Intersect.NOT_INTERSECTS ? t('blocked') : t('allowed');
  const filtersTitle = val.size ? typeLabel : '';

  const switchState = op === Intersect.NOT_INTERSECTS;

  useEffect(() => {
    refetchCategories();
  }, [refetchCategories]);

  const selectedCategories = useMemo(() => [...val], [val]);

  if (isCategoriesLoading) {
    return <Loader />;
  }

  return val.size !== 0 ? (
    <TargetingDropdownSection
      name={t('Category')}
      isEmpty={false}
      onOpenDrawerClick={onOpenDrawerClick}
      onRemove={onRemove}
      hasOptionsSelected={!!optionsCount && optionsCount > 0}
    >
      <div data-test="sub-section">
        <FiltersTitle
          onClear={noop}
          title={t('Category [ {count} ]', { count: val.size })}
          readonly={readonly}
          disabledReasonMessage={disabledReasonMessage}
        />
        <SelectedItems
          filtersTitle={filtersTitle}
          isAddIcon={!switchState}
          selectedItems={selectedCategories}
          readonly={readonly}
          handleChange={noop}
          options={categories}
          dataTest="categories"
        />
      </div>
    </TargetingDropdownSection>
  ) : null;
};
